import { useState, useEffect } from 'react';
import { BsClockHistory, BsEye, BsBox, BsCreditCard, BsCheckCircle, BsXCircle, BsClock } from 'react-icons/bs';
import { motion } from 'framer-motion';
import axios from '../lib/axios';
import { getImageUrl } from '../lib/imageUtils';

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await axios.get('/auth/orders');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <BsCheckCircle className="text-green-400" />;
      case 'cancelled':
        return <BsXCircle className="text-red-400" />;
      case 'pending':
        return <BsClock className="text-yellow-400" />;
      default:
        return <BsBox className="text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'הושלמה';
      case 'cancelled':
        return 'בוטלה';
      case 'pending':
        return 'בטיפול';
      case 'shipped':
        return 'נשלחה';
      default:
        return 'לא ידוע';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B8A61]"></div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-12">
        <BsClockHistory className="w-16 h-16 text-gray-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-[#9B8A61] mb-2">עדיין לא בוצעו הזמנות</h3>
        <p className="text-gray-400">כאשר תבצע הזמנות, הן יוצגו כאן</p>
        <button
          onClick={() => window.location.href = '/'}
          className="mt-4 px-6 py-3 bg-gradient-to-r from-[#9B8A61] to-[#D4AF37] text-white rounded-lg hover:opacity-90 transition-all duration-300"
        >
          התחל לקנות
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-[#9B8A61]">היסטוריית הזמנות</h3>
        <span className="text-gray-400">{orders.length} הזמנות</span>
      </div>

      <div className="grid gap-6">
        {orders.map((order, index) => (
          <motion.div
            key={order._id || index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-gradient-to-br from-[#3a3a3a] to-[#2a2a2a] rounded-xl p-6 border border-[#4a4a4a] hover:border-[#9B8A61] transition-all duration-300"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Order Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-lg font-bold text-[#D4AF37]">
                    הזמנה #{order.orderNumber || order._id?.slice(-6) || `00${index + 1}`}
                  </h4>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(order.status)}
                    <span className="text-sm text-gray-300">
                      {getStatusText(order.status)}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">תאריך הזמנה:</span>
                    <p className="text-gray-300 font-medium">
                      {formatDate(order.createdAt || new Date())}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400">סכום כולל:</span>
                    <p className="text-[#D4AF37] font-bold">
                      ₪{order.totalAmount || '0.00'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-400">מספר פריטים:</span>
                    <p className="text-gray-300 font-medium">
                      {order.items?.length || 0} פריטים
                    </p>
                  </div>
                </div>

                {/* Items Preview */}
                {order.items && order.items.length > 0 && (
                  <div className="mt-4">
                    <p className="text-gray-400 text-sm mb-2">פריטים בהזמנה:</p>
                    <div className="flex flex-wrap gap-2">
                      {order.items.slice(0, 3).map((item, itemIndex) => (
                        <div key={itemIndex} className="bg-[#1a1a1a] rounded-lg px-3 py-1 text-sm">
                          <span className="text-gray-300">{item.name || `פריט ${itemIndex + 1}`}</span>
                          {item.quantity > 1 && (
                            <span className="text-[#9B8A61] mr-1">×{item.quantity}</span>
                          )}
                        </div>
                      ))}
                      {order.items.length > 3 && (
                        <div className="bg-[#1a1a1a] rounded-lg px-3 py-1 text-sm text-gray-400">
                          +{order.items.length - 3} נוספים
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-2 lg:w-48">
                <button
                  onClick={() => setSelectedOrder(selectedOrder === order._id ? null : order._id)}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-[#9B8A61] hover:bg-[#D4AF37] text-white rounded-lg transition-all duration-300"
                >
                  <BsEye />
                  {selectedOrder === order._id ? 'הסתר פרטים' : 'הצג פרטים'}
                </button>
                
                {order.paymentMethod && (
                  <div className="flex items-center justify-center gap-2 px-4 py-2 bg-[#2a2a2a] rounded-lg">
                    <BsCreditCard className="text-[#D4AF37]" />
                    <span className="text-sm text-gray-300">
                      {order.paymentMethod === 'israeli_credit' ? 'כרטיס אשראי' : 'אחר'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Expanded Details */}
            {selectedOrder === order._id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-6 pt-6 border-t border-[#4a4a4a]"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Shipping Info */}
                  <div>
                    <h5 className="text-[#D4AF37] font-medium mb-3">פרטי משלוח</h5>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-400">שם:</span> <span className="text-gray-300">{order.shippingInfo?.name || 'לא זמין'}</span></p>
                      <p><span className="text-gray-400">כתובת:</span> <span className="text-gray-300">{order.shippingInfo?.address || 'לא זמין'}</span></p>
                      <p><span className="text-gray-400">טלפון:</span> <span className="text-gray-300">{order.shippingInfo?.phone || 'לא זמין'}</span></p>
                    </div>
                  </div>

                  {/* Payment Info */}
                  <div>
                    <h5 className="text-[#D4AF37] font-medium mb-3">פרטי תשלום</h5>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-400">אמצעי תשלום:</span> <span className="text-gray-300">{order.paymentMethod === 'israeli_credit' ? 'כרטיס אשראי ישראלי' : 'אחר'}</span></p>
                      <p><span className="text-gray-400">סטטוס תשלום:</span> <span className="text-gray-300">{order.paymentStatus === 'paid' ? 'שולם' : 'ממתין'}</span></p>
                      <p><span className="text-gray-400">מספר הזמנה:</span> <span className="text-gray-300">{order.transactionId || 'לא זמין'}</span></p>
                    </div>
                  </div>
                </div>

                {/* Detailed Items List */}
                {order.items && order.items.length > 0 && (
                  <div className="mt-6">
                    <h5 className="text-[#D4AF37] font-medium mb-3">פירוט פריטים</h5>
                    <div className="space-y-3">
                      {order.items && Array.isArray(order.items) && order.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center justify-between bg-[#1a1a1a] rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            {item.image && (
                              <img
                                src={getImageUrl(item.image || '/images/placeholder.svg')}
                                alt={item.name}
                                className="w-12 h-12 rounded-lg object-cover"
                                onError={(e) => {
                                  e.target.src = '/placeholder.jpg';
                                }}
                              />
                            )}
                            <div>
                              <p className="text-gray-300 font-medium">{item.name || `פריט ${itemIndex + 1}`}</p>
                              <p className="text-gray-400 text-sm">כמות: {item.quantity || 1}</p>
                            </div>
                          </div>
                          <div className="text-left">
                            <p className="text-[#D4AF37] font-bold">₪{item.price || '0.00'}</p>
                                                         <p className="text-gray-400 text-sm">סה&quot;כ: ₪{(item.price * item.quantity) || '0.00'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default OrderHistory; 